import {JournalSession} from '@cp/model';
import {ChevronLeft, ChevronRight, Refresh} from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Stack,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import {Fragment} from 'react';

function groupSessionsByDisplayYear(sessions: JournalSession[]): {
  year: string;
  sessions: JournalSession[];
}[] {
  const byYear = new Map<string, JournalSession[]>();

  sessions.forEach((s) => {
    const year = s.id.length >= 4 ? s.id.slice(0, 4) : 'Other';
    const list = byYear.get(year) ?? [];
    list.push(s);
    byYear.set(year, list);
  });

  return [...byYear.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([year, list]) => ({
      year,
      sessions: [...list].sort((x, y) => y.id.localeCompare(x.id)),
    }));
}

interface AdminTradeSessionsProps {
  journalSessions: JournalSession[];
  selectedSessionId: string | undefined;
  onSelectSessionId: (sessionId: string | undefined) => void;

  journalLoading: boolean;
  journalPageDepth: number;
  journalHasNextPage: boolean;

  onReloadSessions: () => void;
  onSessionsPrev: () => void;
  onSessionsNext: () => void;
}

export default function AdminTradeSessions({
  journalSessions,
  selectedSessionId,
  onSelectSessionId,
  journalLoading,
  journalPageDepth,
  journalHasNextPage,
  onReloadSessions,
  onSessionsPrev,
  onSessionsNext,
}: AdminTradeSessionsProps) {
  const sessionsByYear = groupSessionsByDisplayYear(journalSessions);

  return (
    <Stack spacing={1.5} sx={{mb: 3}}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
        <Typography variant="h6" component="h3">
          Journal sessions
        </Typography>
        <Button
          size="small"
          variant={selectedSessionId === undefined ? 'contained' : 'outlined'}
          onClick={() => onSelectSessionId(undefined)}
          disabled={selectedSessionId === undefined}
          sx={{textTransform: 'none'}}
        >
          Clear session
        </Button>
      </Stack>

      {selectedSessionId !== undefined && (
        <Typography variant="body2" color="text.secondary">
          Trade list filtered by session <strong>{selectedSessionId}</strong>
        </Typography>
      )}

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Button
          variant="outlined"
          size="small"
          startIcon={<Refresh />}
          onClick={() => onReloadSessions()}
          disabled={journalLoading}
          sx={{textTransform: 'none'}}
        >
          Reload sessions
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ChevronLeft />}
          onClick={() => onSessionsPrev()}
          disabled={journalLoading || journalPageDepth === 0}
          sx={{textTransform: 'none'}}
        >
          Sessions prev
        </Button>
        <Button
          variant="outlined"
          size="small"
          endIcon={<ChevronRight />}
          onClick={() => onSessionsNext()}
          disabled={journalLoading || !journalHasNextPage}
          sx={{textTransform: 'none'}}
        >
          Sessions next
        </Button>
        {journalLoading ? <CircularProgress size={22} /> : null}
      </Stack>

      <List
        dense
        disablePadding
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          maxHeight: 360,
          overflow: 'auto',
        }}
      >
        {sessionsByYear.map(({year, sessions}) => (
          <Fragment key={year}>
            <ListSubheader sx={{bgcolor: 'background.paper', lineHeight: '40px'}}>{year}</ListSubheader>
            {sessions.map((s) => (
              <ListItemButton key={s.id} selected={selectedSessionId === s.id} onClick={() => onSelectSessionId(s.id)}>
                <ListItemText
                  primary={s.id}
                  secondary={`${s.tradeCount} trade(s) · updated ${dayjs(s.updatedAt).toISOString()}`}
                />
              </ListItemButton>
            ))}
          </Fragment>
        ))}
      </List>

      {journalSessions.length === 0 && !journalLoading && (
        <Typography variant="body2" color="text.secondary" component="div">
          No journal session rows yet. They are created when trades with a sessionId are written (see Firestore trigger{' '}
          <Box component="span" sx={{fontFamily: 'monospace'}}>
            journalSessionIndexOnTradeWritten
          </Box>
          ). Existing trades only appear after a write to that trade or a backfill.
        </Typography>
      )}
    </Stack>
  );
}
